import { fallbackSrc, sizesFor, srcSet, type Mascot as MascotSpec } from "./mascots";

export interface MascotProps {
  mascot: MascotSpec;
  /**
   * The hero figure is in the first screen, so it must not wait for the viewport. Everything
   * else on this page is below the fold and stays lazy.
   */
  priority?: boolean;
  className?: string;
}

/**
 * The Cryptico mascot, as a decorative accent beside a section heading.
 *
 * THE HERO CARRIES ONE TOO — reversing an earlier rule, by owner decision. It is sized and
 * placed so the product still leads: the figure is about a fifth of the app frame's width and
 * stands at its bottom-right corner, leaning on the composer strip rather than on the
 * conversation. The mascot is kept out of the second showcase band, where a screenshot
 * already carries the whole argument.
 *
 * ⚠ THE SIZES ARE NOT THE OLD ONES. The current character (a bear in a zip hoodie) is drawn
 * tall and narrow where the retired one was squat, so every earlier width would now produce a
 * figure roughly twice as tall — tall enough, in the hero, to reach up over the message
 * bubbles. Widths were re-derived per slot; see mascots.ts and .hero__mascot in sections.css.
 *
 * Four places in total, two of them empty right-hand space beside a section head with no
 * imagery of its own:
 *
 *   hero     — mascot-present, the full figure, leaning on the app frame's bottom-right;
 *   calls    — mascot-phone, the full figure, standing left of the call screenshots;
 *   limits   — face-thinking, next to the list of things we do not promise;
 *   faq      — face-wink, the lightest-toned section on the page.
 *
 * Chosen by tone, not by filling space: a grinning face beside "what we do not promise" would
 * read as a joke at the reader's expense, which is why the one unsmiling frame of the five is
 * reserved for exactly that section.
 *
 * ALT IS EMPTY, ON PURPOSE AND FOR NOW. There are no i18n keys for the mascot yet, and this
 * page forbids user-facing text originating in a component. An empty alt is the correct
 * markup for a decorative image sitting next to a heading that already says what the section
 * is — and it is strictly better than an invented English string that would then be read
 * aloud, untranslated, on all 24 locales.
 *
 * NO ANIMATION. Not "animation behind a prefers-reduced-motion guard" — none at all. The page
 * is strict and engineering, the mascot is there to warm it slightly, and a floating cartoon
 * figure is exactly the thing that would start competing with the copy. Nothing here needs the
 * reduced-motion escape hatch because there is no motion to reduce.
 */
export function Mascot({ mascot, priority = false, className }: MascotProps) {
  return (
    <picture className={["mascot", className].filter(Boolean).join(" ")}>
      <source type="image/avif" srcSet={srcSet(mascot, "avif")} sizes={sizesFor(mascot)} />
      <source type="image/webp" srcSet={srcSet(mascot, "webp")} sizes={sizesFor(mascot)} />
      {/* width/height are the intrinsic size of the default variant: the aspect ratio then
          exists before a byte arrives and the section does not jump as the frame loads.
          Every frame has its own ratio, so these come from the catalogue rather than being
          shared — a common fixed height across frames would squash them. */}
      <img
        className="mascot__img"
        src={fallbackSrc(mascot)}
        width={mascot.width}
        height={mascot.height}
        alt=""
        loading={priority ? "eager" : "lazy"}
        decoding="async"
      />
    </picture>
  );
}
